/**
 * Scroll to the #hash a visitor actually arrived with.
 *
 * The browser jumps to a hash while parsing the HTML. This is an SPA, so at
 * that moment the document is an empty <div id="root"> and there is nothing
 * to jump to — and the browser does not retry once React mounts. Every
 * shared /#menu link, every printed /#visit, and every language switch that
 * carries a hash therefore landed at the top of the page. In-page nav clicks
 * always worked, because by then the section exists, which is why this went
 * unnoticed.
 *
 * So: look for the target after mount, and keep looking briefly, because
 * sections below the fold are still being laid out by lazy images and late
 * fonts. Once found, scroll, then correct once more after the layout settles.
 *
 * Any real scroll input from the visitor cancels the whole thing — nothing
 * here is allowed to fight someone who has started reading.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const FIND_TIMEOUT_MS = 2000;
const POLL_MS = 100;
const SETTLE_MS = 450;

export default function HashScroll() {
  const { hash } = useLocation();

  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;

    let cancelled = false;
    const timers: number[] = [];
    const stop = () => {
      cancelled = true;
      timers.forEach(window.clearTimeout);
    };

    // Passive: we only want to know that it happened, never to block it.
    window.addEventListener('wheel', stop, { passive: true, once: true });
    window.addEventListener('touchstart', stop, { passive: true, once: true });
    window.addEventListener('keydown', stop, { once: true });

    const started = Date.now();
    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (!el) {
        if (Date.now() - started < FIND_TIMEOUT_MS) {
          timers.push(window.setTimeout(attempt, POLL_MS));
        }
        return;
      }
      el.scrollIntoView();
      // One correction after images below finish reserving their space.
      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          document.getElementById(id)?.scrollIntoView();
        }, SETTLE_MS)
      );
    };
    timers.push(window.setTimeout(attempt, 0));

    return () => {
      stop();
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
  }, [hash]);

  return null;
}
